import { ApiError, apiPost, apiPostText } from './apiClient';
import type {
    TraderPerformanceMetrics,
    TraderFinancials,
} from './traderProfileTypes';

export type TraderSearchOp = 'gt' | 'gte' | 'lt' | 'lte' | 'eq' | 'between';

export interface TraderSearchFilter {
    column: string;
    op: TraderSearchOp;
    value: number;
    value2?: number;
}

export type TraderTagMatch = 'any' | 'all';

// The new fields are the advanced-search contract (spec 4.7). Every one is
// optional: a request without them is exactly what the leaderboard sends.
export interface TraderSearchRequest {
    filters?: TraderSearchFilter[];
    sort_by?: string;
    sort_dir?: 'asc' | 'desc';
    limit?: number;
    offset?: number;
    // `0x` + 40 hex, or `@` + an exact Polymarket name. Unknown name → 404.
    address?: string | null;
    active_within_days?: number | null;
    // `YYYY-MM-DD`, compared as UTC midnight.
    first_seen_after?: string | null;
    main_category_include?: string[];
    main_category_exclude?: string[];
    tags_include?: string[];
    tags_exclude?: string[];
    tag_match?: TraderTagMatch;
    // 'advanced' turns on the 120 s budget, limit up to 2,000 (10,000 with
    // `format`) and count_only.
    mode?: 'advanced' | null;
    count_only?: boolean;
    // The download pages: with `format` the answer is not the JSON object
    // below but the rows as text (CSV lines without a header, or JSON lines),
    // limit may go up to 10,000, and `fields` names what each line holds.
    format?: 'csv' | 'json' | null;
    fields?: readonly string[];
}

// Leaner than TraderActivity — search never returned hourly_activity/size_distribution.
export interface TraderSearchActivity {
    first_seen: string;
    last_seen: string;
    active_days: number;
    total_trades: number;
    lifetime_volume_usd: number;
    avg_size_usd: number;
    trade_freq_per_day: number;
    unique_markets: number;
    closed_count: number;
    avg_hold_days: number;
}

// Leaner than TraderBehaviorSignals — search doesn't compute repeat_market_rate/
// off_hours_activity/avg_entry_prob/entry_prob_buckets.
export interface TraderSearchBehaviorSignals {
    algo_likelihood: number;
    capital_utilization: number | null;
    market_liquidity_score: number | null;
}

export interface TraderSearchResult {
    address: string;
    activity: TraderSearchActivity;
    performance: TraderPerformanceMetrics;
    financials: TraderFinancials;
    behavior: TraderSearchBehaviorSignals;
    tags: string[];
    // Why the Data Error tag is set (bogg's sanity violation codes). Optional
    // because an older indexer omits it; missing reads as no reasons.
    data_issues?: string[];
    // The category with the largest volume; null without category volume.
    // Optional because an indexer older than the advanced search omits it.
    main_category?: string | null;
}

// Mirrors the indexer's _SearchResponse. A row request never counts the
// matches: it fetches one row past `limit` and reports whether more pages
// exist. Only a `count_only` request (advanced mode) returns `total`.
export interface TraderSearchResponse {
    has_more: boolean;
    traders: TraderSearchResult[];
    total?: number | null;
}

// An indexer that predates the text download either answers a small page with
// the usual JSON object (it ignores `format`), or refuses the first page of a
// real download: it caps an advanced page at 2,000 rows, a page here is 10,000.
export class ExportUnsupportedError extends Error {
    constructor() {
        super('The indexer does not support this download yet.');
        this.name = 'ExportUnsupportedError';
    }
}

const TEXT_TYPES = { csv: 'text/csv', json: 'application/x-ndjson' };
const OLD_PAGE_LIMIT = /limit must be 1-2000\b/;

// One download page (request.format set): the text the indexer wrote.
export async function searchTraderText(
    request: TraderSearchRequest,
    signal?: AbortSignal
): Promise<string> {
    let answer: { text: string; contentType: string };
    try {
        answer = await apiPostText('/api/trader-search', request, signal);
    } catch (error) {
        if (
            error instanceof ApiError &&
            error.status === 400 &&
            OLD_PAGE_LIMIT.test(error.message)
        ) {
            throw new ExportUnsupportedError();
        }
        throw error;
    }
    const expected = TEXT_TYPES[request.format ?? 'csv'];
    if (!answer.contentType.toLowerCase().startsWith(expected)) {
        throw new ExportUnsupportedError();
    }
    return answer.text;
}

export function searchTraders(
    request: TraderSearchRequest,
    signal?: AbortSignal
): Promise<TraderSearchResponse> {
    return apiPost<TraderSearchResponse>('/api/trader-search', request, signal);
}

// Filters for the "top traders by PnL" overview leaderboard. cat_share_* is a
// 0-1 share of the trader's volume, so this keeps traders with under half of
// their volume in sports.
export const TOP_TRADERS_BY_PNL_FILTERS: TraderSearchFilter[] = [
    { column: 'win_rate', op: 'gte', value: 0.5 },
    { column: 'cat_share_sports', op: 'lt', value: 0.5 },
    { column: 'roi_pct', op: 'lt', value: 1000.0 },
    { column: 'total_pnl', op: 'lt', value: 1000000000.0 },
];

// Cache the top-traders leaderboard for 10 minutes. The underlying /api/trader-search
// call takes ~15-20s and the leaderboard shifts on the order of hours, not seconds,
// so TTL-based memoisation avoids making users wait on repeat navigations.
//
// Two layers:
//   - in-memory (fastest, per-tab, cleared on reload)
//   - localStorage (survives reloads and browser restarts, subject to TTL)
const TOP_TRADERS_TTL_MS = 10 * 60 * 1000;
const TOP_TRADERS_STORAGE_KEY = 'topTradersByPnl:v1';

interface TopTradersCacheEntry {
    response: TraderSearchResponse;
    fetchedAt: number;
    limit: number;
}

let _topTradersCache: TopTradersCacheEntry | null = null;
let _topTradersInflight: Promise<TraderSearchResponse> | null = null;

function readTopTradersFromStorage(limit: number): TopTradersCacheEntry | null {
    if (typeof window === 'undefined') return null;
    try {
        const raw = window.localStorage.getItem(TOP_TRADERS_STORAGE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw) as TopTradersCacheEntry;
        if (parsed.limit !== limit) return null;
        if (Date.now() - parsed.fetchedAt >= TOP_TRADERS_TTL_MS) return null;
        if (!parsed.response || !Array.isArray(parsed.response.traders)) {
            return null;
        }
        return parsed;
    } catch {
        return null;
    }
}

function writeTopTradersToStorage(entry: TopTradersCacheEntry): void {
    if (typeof window === 'undefined') return;
    try {
        window.localStorage.setItem(
            TOP_TRADERS_STORAGE_KEY,
            JSON.stringify(entry)
        );
    } catch {
        // quota exceeded or localStorage disabled — fall back to memory-only
    }
}

export function searchTopTradersByPnl(
    limit = 20
): Promise<TraderSearchResponse> {
    const now = Date.now();

    if (
        _topTradersCache &&
        _topTradersCache.limit === limit &&
        now - _topTradersCache.fetchedAt < TOP_TRADERS_TTL_MS
    ) {
        return Promise.resolve(_topTradersCache.response);
    }

    const persisted = readTopTradersFromStorage(limit);
    if (persisted) {
        _topTradersCache = persisted;
        return Promise.resolve(persisted.response);
    }

    if (_topTradersInflight) return _topTradersInflight;

    _topTradersInflight = searchTraders({
        filters: TOP_TRADERS_BY_PNL_FILTERS,
        sort_by: 'total_pnl',
        sort_dir: 'desc',
        limit,
    })
        .then(response => {
            const entry: TopTradersCacheEntry = {
                response,
                fetchedAt: Date.now(),
                limit,
            };
            _topTradersCache = entry;
            writeTopTradersToStorage(entry);
            _topTradersInflight = null;
            return response;
        })
        .catch(err => {
            _topTradersInflight = null;
            throw err;
        });

    return _topTradersInflight;
}
